import React, { useMemo } from 'react';
import {
  Label,
  HStack,
  Text,
  Card,
  Table,
  Button,
  Tooltip,
} from '@adc/parallax-component-library';
import { ParticipantSymptomNotesConfiguration } from 'adc-sirius-module-common';
import EditParticipantNoteModal from '../../modal/EditParticipantNoteModal';
import {
  studyQueryOptions,
  updateStudyMutateOptions,
} from '../../../queryOptions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Study } from '../../../models';
import './ConfigTable.scss';
import i18n from '../../../utilities/i18n';

type Props = {
  study: Study;
  canEdit: boolean;
};

const formatDuration = (rawValueMinutes) => {
  const rawValue = rawValueMinutes ?? 0;
  const hours = Math.floor(rawValue / 60);
  const minutes = rawValue % 60;

  let formattedValue = '';

  if (hours > 0) {
    formattedValue += `${hours}${i18n.t('Common.unitHours_short')}`;
  }

  if (minutes > 0) {
    formattedValue += `${minutes}${i18n.t('Common.unitMinutes_short')}`;
  }

  return formattedValue;
};

/**
 * ConfigTable component
 * @param {Study} study The study object
 * @param {boolean} canEdit Whether the user can edit the configuration
 * @returns {React.JSX.Element}
 */
export const ConfigTable = ({ study, canEdit }: Props) => {
  const updateStudy = useMutation(updateStudyMutateOptions());
  const queryClient = useQueryClient();
  const participantSymptomNotes = study.configuration?.participantSymptomNotes;
  const choices = participantSymptomNotes?.choices ?? [];
  const fullChoicesText =
    choices.length > 0
      ? choices.join(', ')
      : i18n.t('Page_Study.table_config.notConfigured');

  const onSave = (
    values: ParticipantSymptomNotesConfiguration,
    updateReason: string,
  ) =>
    updateStudy
      .mutateAsync({
        studyKey: study.key,
        updatedStudyValues: {
          ...study,
          updateReason,
          configuration: {
            ...study.configuration,
            participantSymptomNotes: { ...values },
          },
        },
      })
      .then(() => {
        const studyQueryKey = studyQueryOptions({
          key: study.key,
        }).queryKey;

        queryClient.invalidateQueries({ queryKey: studyQueryKey });
      });

  const enabledLabel = useMemo(
    () => (
      <Label
        aria-label={i18n.t('Modal_ParticipantNote.enabled')}
        variant="tertiary"
      >
        <Label.Text type="$bodySmall.medium">
          {i18n.t('Modal_ParticipantNote.enabled')}
        </Label.Text>
        <Label.Text> </Label.Text>
        <Label.Text>
          {i18n.t(`Common.${participantSymptomNotes?.enabled ? 'yes' : 'no'}`)}
        </Label.Text>
      </Label>
    ),
    [participantSymptomNotes?.enabled],
  );

  const intervalPromptsLabel = useMemo(() => {
    if (participantSymptomNotes?.enabled) {
      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.intervalPrompt')}
          variant="tertiary"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.intervalPrompts')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>
            {i18n.t(
              `Common.${participantSymptomNotes.intervalPrompts ? 'yes' : 'no'}`,
            )}
          </Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.enabled,
    participantSymptomNotes?.intervalPrompts,
  ]);

  const intervalFrequencyLabel = useMemo(() => {
    if (
      participantSymptomNotes?.enabled &&
      participantSymptomNotes?.intervalPrompts
    ) {
      const formattedValue = formatDuration(
        participantSymptomNotes?.intervalFrequencyMinutes,
      );

      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.intervalFrequency')}
          variant="tertiary"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.intervalFrequency')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>{`${formattedValue || i18n.t('Page_Study.table_config.notConfigured')}`}</Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.enabled,
    participantSymptomNotes?.intervalFrequencyMinutes,
    participantSymptomNotes?.intervalPrompts,
  ]);

  const reminderFrequencyLabel = useMemo(() => {
    if (
      participantSymptomNotes?.enabled &&
      participantSymptomNotes?.intervalPrompts
    ) {
      const formattedValue = formatDuration(
        participantSymptomNotes?.reminderFrequencyMinutes,
      );

      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.reminderFrequency')}
          variant="tertiary"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.reminderFrequency')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>{`${formattedValue || i18n.t('Page_Study.table_config.notConfigured')}`}</Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.enabled,
    participantSymptomNotes?.intervalPrompts,
    participantSymptomNotes?.reminderFrequencyMinutes,
  ]);

  const choicesLabel = useMemo(() => {
    if (participantSymptomNotes?.enabled) {
      return (
        <Tooltip text={fullChoicesText}>
          <Label
            aria-label={i18n.t('Modal_ParticipantNote.choices')}
            variant="tertiary"
          >
            <Label.Text type="$bodySmall.medium">
              {i18n.t('Modal_ParticipantNote.choices')}
            </Label.Text>
            <Label.Text> </Label.Text>
            <Label.Text>{`${fullChoicesText}`}</Label.Text>
          </Label>
        </Tooltip>
      );
    } else {
      return undefined;
    }
  }, [fullChoicesText, participantSymptomNotes?.enabled]);

  const allowMultiSelectLabel = useMemo(() => {
    if (participantSymptomNotes?.enabled) {
      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.allowMultiSelect')}
          variant="tertiary"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.allowMultiSelect')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>
            {i18n.t(
              `Common.${participantSymptomNotes?.allowMultiSelect ? 'yes' : 'no'}`,
            )}
          </Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [
    participantSymptomNotes?.allowMultiSelect,
    participantSymptomNotes?.enabled,
  ]);

  const showOtherLabel = useMemo(() => {
    if (participantSymptomNotes?.enabled) {
      return (
        <Label
          aria-label={i18n.t('Modal_ParticipantNote.showOther')}
          variant="tertiary"
        >
          <Label.Text type="$bodySmall.medium">
            {i18n.t('Modal_ParticipantNote.showOther')}
          </Label.Text>
          <Label.Text> </Label.Text>
          <Label.Text>
            {i18n.t(
              `Common.${participantSymptomNotes?.showOther ? 'yes' : 'no'}`,
            )}
          </Label.Text>
        </Label>
      );
    } else {
      return undefined;
    }
  }, [participantSymptomNotes?.enabled, participantSymptomNotes?.showOther]);

  return (
    <Card
      title={i18n.t('Page_Study.table_config.title')}
      titleAriaLevel={1}
      titleTypeToken="$h4.strong"
    >
      <Table
        aria-label={i18n.t('Page_Study.table_config.description')}
        minHeight={0}
        height="auto"
        maxHeight="100%"
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {i18n.t('Page_Study.table_config.header.name')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {i18n.t('Page_Study.table_config.header.description')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {i18n.t('Page_Study.table_config.header.configuration')}
            </Table.HeaderCell>
            {canEdit ? <Table.HeaderCell> </Table.HeaderCell> : null}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.TextCell>
              {i18n.t('Page_Study.table_config.participant_notes.title')}
            </Table.TextCell>
            <Table.TextCell>
              <Text type="$bodySmall.default">
                {i18n.t(
                  'Page_Study.table_config.participant_notes.description',
                )}
              </Text>
            </Table.TextCell>
            <Table.TextCell>
              <HStack gap="$1" className={'participant-note-configurations'}>
                {enabledLabel}
                {intervalPromptsLabel}
                {intervalFrequencyLabel}
                {reminderFrequencyLabel}
                {choicesLabel}
                {allowMultiSelectLabel}
                {showOtherLabel}
              </HStack>
            </Table.TextCell>
            {canEdit ? (
              <Table.TextCell>
                <EditParticipantNoteModal
                  initialValues={participantSymptomNotes}
                  trigger={
                    <Button
                      variant="tertiary"
                      size="small"
                      label={i18n.t('Common.edit')}
                      onPress={() => {}}
                      testID="edit-participant-note-button"
                    />
                  }
                  onSuccess={onSave}
                />
              </Table.TextCell>
            ) : (
              <Table.TextCell> </Table.TextCell>
            )}
          </Table.Row>
        </Table.Body>
      </Table>
    </Card>
  );
};
